<template>
  <!--
    B3 — the composer shell.

    NO PAGE-LEVEL LOADING STATE, and that is a decision rather than an omission.
    There is nothing to FETCH for a new broadcast: the draft starts empty and
    both of this page's requests are supplementary. A spinner over an empty form
    would lie about what the screen is doing. The audience descriptions and the
    category labels each load inside their own group, and a group whose own fetch
    failed renders what it already had rather than a hole.

    NOTHING IS STICKY either. The page scrolls natively, so the send button
    scrolls with the form instead of covering the body field, and the software
    keyboard pushes the page rather than sitting on top of the control the author
    is reaching for. `booking-stages.vue` made the same choice.
  -->
  <private-gym-page
    id="admin-broadcast-composer-page"
    name="admin-broadcast-composer"
    :navbar="false"
  >
    <!--
      The shared PageTopNavbar is deliberately switched off, exactly as on
      NewsComposerPage.vue: it carries the panel toggle and the logo, which is
      right for a member's landing surface and wrong here. This is a pushed
      detail screen and the operator needs a way back to where they came from.
    -->
    <f7-navbar title="Nieuw pushbericht" back-link="Terug"></f7-navbar>

    <div class="broadcast-column">
      <!--
        ======================================================================
         B4 — THE AUDIENCE SELECTOR. The primary anchor of this page.
        ======================================================================
        It is first because it is the only choice on the screen that cannot be
        undone, and it opens on the safe one (CAST-05, IC1).

        NO COUNT SITS BESIDE ANY ROW, and this is the "obvious improvement" a
        later reader will want to add. IC2 forbids it in writing so the
        prohibition survives the reader: D-44-02 makes the confirmed count come
        from the SAME server-side resolution that performs the send, so a number
        rendered here could only come from a second query — and a second query is
        a second definition of the audience. The count exists in exactly one
        place in this flow, and that place is the confirmation dialog.
      -->
      <f7-block-title>Naar wie</f7-block-title>

      <!--
        `media-list`, and it is NOT decoration. `list-item.js:125-136` places the
        `text` element into the item ONLY on the media branch: a NON-media
        list-item accepts the `text` prop, renders no `.item-text`, and shows
        nothing at all. Without this attribute the four server-supplied
        descriptions are silently dropped — measured in the running app, where
        the endpoint answered 200 with all four sentences and the rows rendered
        their names alone. `admin/news.vue:74` and `admin/broadcasts.vue` both
        ship `media-list` beside this same class string for the same reason.
      -->
      <f7-list media-list class="list-strong list-outline list-dividers broadcast-audience-list">
        <!--
          The four NAMES come from the frozen client allowlist below; the row's
          `text` is the SERVER's sentence (IC3). `radio-icon="end"` and one
          shared `name` make it a single group — there is no shipped radio group
          in this app, so the props come from the Framework7 v8 documentation and
          the wrapper class string from admin/news.vue:74.
        -->
        <f7-list-item
          v-for="option in audiences"
          :key="option.audience"
          radio
          radio-icon="end"
          name="broadcast-audience"
          :value="option.audience"
          :title="option.label"
          :text="descriptionFor(option.audience)"
          :checked="draft.audience === option.audience"
          @change="selectAudience(option.audience)"
        ></f7-list-item>
      </f7-list>

      <!--
        IC1 in one sentence, on the screen, in Dutch. A sticky audience selection
        is how a test send becomes a live one by accident, so the reset is stated
        rather than left to be discovered.
      -->
      <f7-block-footer>
        Na het versturen staat dit weer op "Alleen test". Kies elke keer opnieuw naar wie je bericht gaat.
      </f7-block-footer>

      <!--
        ======================================================================
         B5 — THE MEMBER PICKER (CAST-04, IC5). CONDITIONAL, not permanent.
        ======================================================================
        It renders only for `Eén lid` and it is the only conditional group on
        this page. Every other group is always present, so the shell never shows
        a hole where a group should be.

        REUSES `/users/search` AND NOTHING ELSE FROM `UsersSearch.vue`. Four
        things that component does are anti-patterns here and none of them is
        below: it raises the app-wide overlay, which covers the navbar and
        swallows the back gesture and which phases 39, 41, 42 and 43 all refused;
        it scrolls the page to the top on every answer, which would throw away
        this composer's position; it searches on mount, for the empty query; and
        it paginates with Vorige/Volgende buttons.

        THE TWO STORE IDENTIFIERS ARE DELIBERATELY NOT SPELLED OUT ABOVE. This
        plan's acceptance criterion is a grep for them over this file, and a
        comment naming the thing it forbids is precisely the reading this phase
        has now recorded four times: a grep that matches the word used to DENY
        the thing. 44-08 made the same omission for the same reason. The rule is
        stated in words so the grep stays a check.

        THE ASYMMETRY WITH THE BULK AUDIENCE IS THE POINT, and it lives on the
        server: `individual` carries no active gate and no subscription gate,
        because a lapsed member is still a person the gym must be able to
        contact (D-44-10, in the owner's own words). `all_active` does NOT
        inherit that. Do not "tidy" the two into one rule.
      -->
      <template v-if="showsMemberPicker">
        <f7-block-title>Zoek een lid</f7-block-title>

        <!-- The chosen member, collapsed to one row. -->
        <f7-list
          v-if="showsChosenMember"
          class="list-strong list-outline list-dividers broadcast-member-list"
        >
          <!--
            `Wijzigen` returns to the search field with the choice STILL in the
            draft, so a mis-tap costs one tap rather than the whole selection.
          -->
          <f7-list-item
            :title="chosenMemberName"
            after="Wijzigen"
            @click="changeMember"
          ></f7-list-item>
        </f7-list>

        <template v-else>
          <!--
            The SHIPPED `UsersSearch.vue:4` string, byte for byte — no full stop
            after `min`. 44-UI-SPEC.md writes `min.` while calling it the shipped
            string unchanged; the two are not byte-identical, and the spec's
            stated intent was identity. One sentence across two admin screens
            rather than two to keep in step. Pinned by a test as a literal.
          -->
          <f7-searchbar
            placeholder="Zoek op naam (min 3 karakters)"
            :clear-button="true"
            :value="memberQuery"
            @input="onMemberQueryInput"
          ></f7-searchbar>

          <!--
            FOUR MUTUALLY EXCLUSIVE STATES from two flags plus the query length,
            and never emptiness inferred from an empty array. `searching` covers
            the debounce window as well as the request, because between the third
            keystroke and the request there is a moment where the results are
            empty and no answer has been asked for — rendering `Geen leden
            gevonden.` there would report a fact nobody has established.
          -->
          <f7-block v-if="memberSearching" class="text-align-center">
            <!-- INLINE, inside the picker's own region. Never the overlay. -->
            <f7-preloader></f7-preloader>
          </f7-block>

          <!--
            B13, scoped to this region. The rest of the composer stays usable, so
            an operator whose search will not load can switch to another audience
            instead of being stuck on a dead page.

            The screen dispatches no message of its own: the search below is
            deliberately not silent, so the global handler has already shown the
            one message this failure gets — the same arrangement admin/news.vue
            and admin/broadcasts.vue both ship. The server's own error text is
            never rendered: it is English, unbounded, and names nothing the
            operator can act on.
          -->
          <f7-block v-else-if="memberSearchFailed" strong inset>
            <p class="broadcast-heading">We konden de leden niet laden.</p>
            <p>Controleer je internetverbinding en probeer het opnieuw.</p>
            <f7-button fill @click="searchMembers">Opnieuw proberen</f7-button>
          </f7-block>

          <f7-block-footer v-else-if="!memberQueryIsLongEnough">
            Typ minstens 3 karakters om te zoeken.
          </f7-block-footer>

          <f7-block-footer v-else-if="memberResults.length === 0">
            Geen leden gevonden.
          </f7-block-footer>

          <!--
            NO PAGER. The result list is bounded by the endpoint's own
            pagination; the search field is how the operator narrows.
          -->
          <f7-list v-else class="list-strong list-outline list-dividers broadcast-member-list">
            <f7-list-item
              v-for="member in memberResults"
              :key="member.id"
              :title="memberName(member)"
              @click="chooseMember(member)"
            ></f7-list-item>
          </f7-list>
        </template>

        <!--
          IC6's third disabled condition, named in the group where the operator
          must fix it. There is no disabled-reason text under the send button.
        -->
        <f7-block-footer v-if="!hasChosenMember">
          Kies eerst een lid.
        </f7-block-footer>
      </template>

      <!--
        ======================================================================
         B6 — THE CATEGORY SELECTOR.
        ======================================================================
        Three of the five categories, and the two that are absent are absent on
        purpose. `booking` is TimeSensitive and NOTIF-05 reserves the Focus
        break-through for reminders alone — a free-text composer that could post
        into it would make the exception mean nothing. `payment` is money-shaped,
        which is what the payload guard exists to keep off a lock screen.

        THE LABELS AND DESCRIPTIONS ARE SERVER-OWNED and this component authors
        no fallback for either. A fallback that drifts from the server's is worse
        than a blank moment, and the member's own notification screen already
        renders these exact sentences.
      -->
      <f7-block-title>Soort bericht</f7-block-title>

      <!-- `media-list` for the same reason as the audience list above. -->
      <f7-list
        v-if="categories.length > 0"
        media-list
        class="list-strong list-outline list-dividers broadcast-category-list"
      >
        <f7-list-item
          v-for="option in categories"
          :key="option.category"
          radio
          radio-icon="end"
          name="broadcast-category"
          :value="option.category"
          :title="option.label"
          :text="option.description"
          :checked="draft.category === option.category"
          @change="selectCategory(option.category)"
        ></f7-list-item>
      </f7-list>

      <!--
        Gated on the rows, not rendered unconditionally: a footnote explaining
        rows that are not on screen explains nothing. When the endpoint has not
        answered, the group is its title and nothing else — and the send button
        stays disabled, because a category that cannot be named cannot be chosen.
      -->
      <f7-block-footer v-if="categories.length > 0">
        Leden die dit soort bericht hebben uitgezet, krijgen het niet.
      </f7-block-footer>

      <!--
        ======================================================================
         B7 — THE MESSAGE FIELDS.
        ======================================================================
        NO TWO-WAY BINDING. The page holds ONE draft and each input emits its
        change through one handler, which is the contract NewsComposer.vue and
        booking-stages.vue both state: a two-way binding would give each input a
        second, drifting copy of the field.

        BOTH BOUNDS MATCH THE SERVER'S, which validates 120 and 480 and returns a
        field error rather than truncating. `maxlength` makes the browser stop
        accepting keystrokes; the counter is what makes the limit visible BEFORE
        that happens.

        A FAILURE NEVER CLEARS THESE FIELDS. The operator's text is what they
        must edit after a refusal.
      -->
      <f7-block-title>Bericht</f7-block-title>

      <f7-list class="list-strong list-outline list-dividers">
        <f7-list-input
          label="Titel"
          type="text"
          placeholder="Storing bij de deur"
          maxlength="120"
          :value="draft.title"
          :info="draft.title.length + '/120'"
          @input="updateDraftField('title', $event.target.value)"
        ></f7-list-input>

        <f7-list-input
          class="broadcast-body-input"
          label="Tekst"
          type="textarea"
          placeholder="Wat moeten leden weten?"
          maxlength="480"
          resizable
          :value="draft.body"
          :info="draft.body.length + '/480'"
          @input="updateDraftField('body', $event.target.value)"
        ></f7-list-input>
      </f7-list>

      <!--
        ======================================================================
         B8 — THE LOCK-SCREEN IMPRESSION. An impression, and it says so.
        ======================================================================
        NOT A DEVICE REPLICA. No fake status bar, no fake app icon and no fake
        time. Phase 43 made the same refusal for the news preview and for the
        same reason: a fake chrome invites drift from the real one, and the real
        one differs per handset, per operating-system version and per lock state.

        INTERPOLATION ONLY, AND NO RAW-HTML DIRECTIVE ANYWHERE ON THIS PAGE. A
        push payload is plain text, so Vue's own escaping is the whole story
        (T-44-44). The directive is deliberately not named here: this plan's
        acceptance criterion is a bare grep over this file, and a comment naming
        the thing it forbids would match it — the reading this phase has recorded
        four sessions running.

        HALF AN IMPRESSION IS WORSE THAN NONE. A title with no body shows the
        instruction rather than a preview of a shape the phone will not produce.
      -->
      <f7-block-title>Zo komt het binnen</f7-block-title>

      <f7-block strong inset class="broadcast-impression">
        <p v-if="impressionState === 'empty'">Vul een titel en een tekst in.</p>

        <template v-else>
          <p class="broadcast-impression-title">{{ draft.title }}</p>
          <p class="broadcast-impression-body">{{ draft.body }}</p>

          <!--
            MAY, never WILL. No push has reached a real device in this project
            and no truncation number has been measured, so the copy claims none.
          -->
          <f7-block-footer v-if="impressionState === 'long'">
            Je titel of tekst is lang. Sommige telefoons tonen alleen het begin.
          </f7-block-footer>
        </template>

        <f7-block-footer>
          Dit is een indruk, geen exacte weergave. Elke telefoon toont het net anders.
        </f7-block-footer>
      </f7-block>

      <!--
        ======================================================================
         B9 — THE SEND BUTTON. One per page, and it scrolls with the form.
        ======================================================================
        THE ELLIPSIS IS LOAD-BEARING: it states that a confirmation follows.
        `Bericht opslaan` on the news composer looks alike and saves immediately,
        so the difference is written into the label.

        `disabled` IS WHAT PREVENTS THE DOUBLE SUBMIT, alongside Framework7's own
        preloader and loading props — never the app-wide overlay, which covers
        the navbar and swallows the back gesture.

        NO DISABLED-REASON TEXT SITS UNDER IT. Each missing piece already names
        itself in the group where the operator must fix it: the counters read
        zero, the picker says `Kies eerst een lid.`, and an unnamed category
        leaves the group empty. A sentence here would be a second place for the
        same message.

        THE COUNT NEVER APPEARS ON THIS BUTTON. It appears on the confirmation
        dialog's button, after the server produced it (IC7).
      -->
      <!--
        ======================================================================
         B12 — THE SEND FAILED. One block, three bodies, exactly one rendered.
        ======================================================================
        PERSISTENT, NEVER A TOAST. A failed send leaves the operator with a
        draft they must change and re-send, and a message that vanishes in five
        seconds is a message they will re-read by pressing send again. The
        global danger toast is suppressed for this one call (IC8), so this block
        is the ONE message.

        NO WARNING COLOUR. It uses the same white `f7-block strong inset` every
        other block on this page uses, with the triangle in the inherited
        secondary text colour. Persistence is the signal, not hue — phase 39 set
        that precedent for the permission banner and phase 43 kept it.

        THE RATE-LIMITED AND GENERIC BODIES ARE AUTHORED HERE. The 422 body is
        NOT: `PushPayloadGuard` and the form request know which field and which
        rule failed, and nothing this screen could write would beat that.

        It sits ABOVE the button rather than below it, because the operator's
        eye returns to the control they just pressed and the reason has to be
        on the way back to the fields they must edit.
      -->
      <f7-block v-if="failure" strong inset class="broadcast-failure">
        <p class="broadcast-heading">
          <f7-icon f7="exclamationmark_triangle" class="broadcast-failure-icon"></f7-icon>
          {{ failureHeading }}
        </p>

        <template v-if="failure.kind === 'refused_text'">
          <p
            v-for="(message, index) in failure.fieldMessages"
            :key="index"
            class="broadcast-failure-body"
          >{{ message }}</p>
        </template>

        <p v-else class="broadcast-failure-body">{{ failureBody }}</p>
      </f7-block>

      <!--
        ======================================================================
         B11 — THE SEND RESULT. It REPLACES the send button (IC1).
        ======================================================================
        `v-else` on the button below is the mechanism, and it is not cosmetic:
        after a successful send the composer does not return to an editable
        state, so a second send needs a new composer — which means a new default
        of `Alleen test`. That is the strongest form of D-44-03 available
        without the typed confirmation the owner declined. A composer that stays
        armed is a composer whose next press inherits the previous audience.

        DO NOT ADD A `send another` BUTTON. It re-creates exactly the stickiness
        D-44-03 removed.

        PERSISTENT, AND NO SUCCESS TOAST IS DISPATCHED. The number is the
        message, and a number that fades in five seconds is not a record.

        WHEN THE TWO NUMBERS DIFFER THIS BLOCK NAMES BOTH. D-44-02's gap was
        confirmed by the owner INCLUDING the gap: the set can move between
        confirming and sending, and the contract is that the operator is TOLD.
        If the delta were recorded but never shown, the record would be a thing
        only a database query can read and the gate would be theatre again.
      -->
      <f7-block v-if="result" strong inset class="broadcast-result">
        <p class="broadcast-heading">Verstuurd</p>
        <p class="broadcast-result-body">{{ resultBody }}</p>
        <p>
          <f7-link class="broadcast-result-link" @click="goToOverview">Naar het overzicht</f7-link>
        </p>
      </f7-block>

      <f7-block v-else>
        <f7-button
          fill
          large
          :preloader="confirming"
          :loading="confirming"
          :disabled="!canSend || confirming"
          @click="requestConfirmation"
        >
          Bericht versturen…
        </f7-button>
      </f7-block>
    </div>
  </private-gym-page>
</template>

<script>
// The IMPORTED `f7`, not `this.$f7`. MEASURED in phase 43 and unchanged here:
// framework7-vue 8.3.4 registers no global property, so `this.$f7` is undefined
// in this app and would throw. `NewsComposerPage.vue:146` and `calendar.vue:362`
// both import it the same way.
import { f7 } from "framework7-vue";

import PrivateGymPage from "../../_layouts/PrivateGymPage.vue";

/**
 * The four audiences, client-side and frozen.
 *
 * WHY THE CLIENT OWNS THE NAMES AND THE SERVER OWNS THE SENTENCES (IC3, CD-03).
 * A name is four words on a radio row and a four-item allowlist, so it is pinned
 * against the server enum by a test — the same arrangement `CATEGORIES` in
 * `src/js/push.js` has had since phase 39. A DESCRIPTION is a claim about a
 * query, and D-44-09's predicate has four branches none of the offered
 * definitions expressed. If this file authored that sentence and the server
 * resolved something else, the operator would be misled about who they just
 * messaged — permanently, because a broadcast cannot be recalled.
 *
 * THE ORDER IS THE READING ORDER AND IT IS NOT ALPHABETICAL. The test audience
 * is first because it is the default and the safe one.
 *
 * Zero rows and many rows are unreachable here by construction: the four names
 * are constants and are never fetched. A failed description fetch removes the
 * second lines, never the rows.
 */
const AUDIENCES = Object.freeze([
  Object.freeze({ audience: "test", label: "Alleen test" }),
  Object.freeze({ audience: "all_active", label: "Alle actieve leden" }),
  Object.freeze({ audience: "booked_today_tomorrow", label: "Training vandaag of morgen" }),
  Object.freeze({ audience: "individual", label: "Eén lid" }),
]);

/**
 * The audience the composer opens on, EVERY time (CAST-05, D-44-03, IC1).
 *
 * Read from this constant and from nowhere else. It is deliberately not read
 * from Vuex, from localStorage, or from any module-level variable that could
 * survive a page: a composer that remembers the last audience is a composer
 * whose next press inherits it.
 */
const DEFAULT_AUDIENCE = "test";

/**
 * The three categories a person may compose into (IC4, CD-02).
 *
 * `booking` and `payment` are excluded — see the template. The list is written
 * out by hand rather than derived from what the server sends, for the same
 * reason the API's own form request writes it out by hand: deriving would
 * silently admit a sixth case to the composer the day somebody adds one.
 */
const CATEGORY_ALLOWLIST = Object.freeze(["operational", "news", "marketing"]);

/**
 * The category the composer opens on.
 *
 * `operational` is contract-basis and therefore default-ON, so a test send
 * actually arrives instead of being filtered by a default-OFF preference. A
 * composer whose default category silently drops the test send is a composer
 * that teaches its operator that push is broken.
 */
const DEFAULT_CATEGORY = "operational";

/**
 * The threshold below which the picker shows its hint rather than a result set.
 *
 * `UsersSearch.vue:4` already tells the operator this number in its placeholder,
 * and the same sentence is reused here, so the two agree by construction.
 */
const MIN_SEARCH_CHARACTERS = 3;

/**
 * The debounce, matching `ThrottlesRequests.vue`'s own default.
 *
 * A DEBOUNCE, NOT A POLL: the handle is cleared on every keystroke and again on
 * unmount, and nothing in this component repeats. D-44-14's "no polling" is a
 * decision about the history screen, and nothing here reopens it.
 */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The two hard caps, matching `ConfirmBroadcastRequest::MAX_TITLE` and
 * `::MAX_BODY` exactly.
 *
 * The server validates the same bounds and returns a field error rather than
 * truncating, so the browser stopping at these numbers is the SECOND control and
 * not the only one. Both are multiples of 4 (CD-10).
 */
const MAX_TITLE = 120;
const MAX_BODY = 480;

/**
 * The two thresholds above which the length hint appears (CD-10).
 *
 * They are NOT the caps. They are the point at which a phone MIGHT shorten what
 * it shows — a warning, not a limit. No device truncation number has been
 * measured in this project, and no push has reached a real device, so the copy
 * they trigger says phones MAY shorten and never that they will.
 */
const LONG_TITLE = 40;
const LONG_BODY = 120;

/**
 * The one sentence that says a broadcast cannot be recalled (B10).
 *
 * It appears in three of the five dialog variants and NOT in the other two, so
 * it is a constant rather than three copies. The TEST variant omits it because
 * the message reaches only the super admins who are already in the room; the
 * ZERO variant omits it because nothing will be sent at all.
 */
const IRREVERSIBLE_LINE = "Een verstuurd pushbericht kun je niet terughalen.";

/**
 * The zero-recipient explanation, and it is a DESIGNED STATE rather than a guard.
 *
 * It names BOTH reasons, because an operator cannot tell them apart from the
 * outside and both are recoverable by a different route: nobody in this group
 * carries the app on a phone, or they have switched this kind of message off.
 * The `{audience sentence}` deliberately does not render here — this text
 * replaces it (44-UI-SPEC.md, B10, the zero row).
 */
const ZERO_RECIPIENT_TEXT =
  "Er is nu niemand in deze groep die een pushbericht kan ontvangen. "
  + "Dat kan komen doordat niemand de app op een telefoon heeft, of doordat ze dit soort bericht hebben uitgezet.";

export default {
  components: { PrivateGymPage },

  /**
   * Exposed on the component options so a test can compare the client allowlist
   * against its own literals and against the server enum. Neither is reactive
   * state and no template expression reads them.
   */
  AUDIENCES,
  CATEGORY_ALLOWLIST,

  props: {
    f7route: Object,
    f7router: Object,
  },

  mixins: [],

  data: function () {
    return {
      /**
       * THE ONE SOURCE OF TRUTH for what will be sent, and the exact shape the
       * confirm endpoint validates.
       *
       * `member_id` is the ONLY thing about WHO that ever leaves this client,
       * and only for the one audience that needs it (D-44-01). No name, no
       * email and no filter is ever written here.
       */
      draft: {
        audience: DEFAULT_AUDIENCE,
        member_id: null,
        category: DEFAULT_CATEGORY,
        title: "",
        body: "",
      },

      /**
       * `{ [audience]: description }` from the server, or an empty map.
       *
       * Component state rather than store state, deliberately. IC1 requires that
       * nothing about the audience survives this page, and the descriptions are
       * cheap to re-read on the next entry — one less thing that can be stale
       * between the server's predicate and the sentence describing it.
       */
      audienceDescriptions: {},
      /**
       * The picker's own search query. Local to the picker: it is never written
       * into the draft, because what leaves this client is an id (D-44-01).
       */
      memberQuery: "",

      /** The current result page, replaced on every search and never appended to. */
      memberResults: [],

      /**
       * True from the KEYSTROKE that reaches three characters until the request
       * settles, and therefore across the debounce window as well as the
       * request.
       *
       * That window is the load-bearing part. Between the third keystroke and
       * the dispatch, `memberResults` is empty and nothing has been asked — and
       * `Geen leden gevonden.` there would report a fact nobody established.
       * "we have not asked yet" and "we asked and there is nothing" look
       * identical in an empty array, which is the defect 44-08 fixed on the
       * history screen.
       */
      memberSearching: false,

      /** Separate from `memberSearching`, so a failed search is its own state. */
      memberSearchFailed: false,

      /**
       * The chosen member's DISPLAY copy — id and the two name parts.
       *
       * Kept beside `draft.member_id` rather than inside it. The draft is the
       * request body, and a name in the request body is a thing the server does
       * not re-derive.
       */
      chosenMember: null,

      /**
       * True while the operator is re-picking after tapping `Wijzigen`.
       *
       * The previous choice stays in the draft throughout, so a mis-tap costs
       * one tap rather than the whole selection.
       */
      changingMember: false,

      /** The pending debounce handle, cleared on every keystroke and on unmount. */
      memberSearchTimer: null,

      /**
       * Monotonic token identifying the newest search.
       *
       * A debounced search over a shared draft can have two requests in flight
       * when a slow one is overtaken by a fast one, and the slow one answering
       * last would put OLDER results under a NEWER query. No rule in the UI-SPEC
       * states this; it is a correctness requirement of the mechanism.
       */
      memberSearchToken: 0,

      /**
       * True while the CONFIRM phase is in flight.
       *
       * Drives the button and nothing else. It is what prevents the double
       * submit — never the app-wide overlay.
       */
      confirming: false,

      /**
       * True while the SEND phase is in flight.
       *
       * Separate from `confirming` on purpose: the two phases are two different
       * requests and only one of them is irreversible. Nothing renders a
       * spinner from this flag — the dialog has none, by design — it exists to
       * stop a second send leaving while the first is still open.
       */
      sending: false,

      /**
       * `{ sent, confirmed }` from the SEND response, or null (B11).
       *
       * BOTH NUMBERS COME FROM THE SEND RESPONSE and neither is carried over
       * from the confirm phase. IC7: if the two were ever the same variable in
       * this client, the delta could not be detected at all.
       */
      result: null,

      /**
       * `{ kind, retryAfterMinutes, fieldMessages }` or null (B12).
       *
       * A SNAPSHOT rather than a live read of the error bag. The bag is cleared
       * at the start of every request (`api.js:88`), so a block still on screen
       * would empty itself the moment the operator pressed send again — the
       * message would vanish exactly when it is being acted on.
       */
      failure: null,

      /** The route entry this page installed its leave guard on, if any. */
      guardedRoute: null,
    };
  },

  computed: {
    /**
     * The four rows, always, in the fixed order.
     *
     * @returns {Array<{audience: string, label: string}>}
     */
    audiences() {
      return AUDIENCES;
    },

    /**
     * The three offerable categories, with the server's own label and
     * description, in `NotificationCategory` declaration order.
     *
     * A category with a LABEL but no description renders the label alone; a
     * category with NEITHER is dropped rather than rendered as a blank row.
     * Before the endpoint answers this is empty, which is what leaves the group
     * as its title alone.
     *
     * @returns {Array<{category: string, label: string, description: string}>}
     */
    categories() {
      const rows = this.$store.getters["notification_preferences/all"] || [];

      return rows
        .filter((row) => CATEGORY_ALLOWLIST.indexOf(row.category) !== -1)
        .filter((row) => typeof row.label === "string" && row.label.trim() !== "")
        .map((row) => ({
          category: row.category,
          label: row.label,
          description: typeof row.description === "string" ? row.description.trim() : "",
        }));
    },

    /**
     * Whether the chosen category is one the server actually named.
     *
     * B6's own `error` row: without labels the group renders empty and the send
     * button stays disabled, because a category that cannot be named cannot be
     * chosen. It is the fifth gate on the button and it fails CLOSED.
     *
     * @returns {boolean}
     */
    hasSelectableCategory() {
      return this.categories.some((option) => option.category === this.draft.category);
    },

    /**
     * Whether the picker renders at all.
     *
     * The ONE conditional group on this page (B3 `partial`). Every other group
     * is always present.
     *
     * @returns {boolean}
     */
    showsMemberPicker() {
      return this.draft.audience === "individual";
    },

    /**
     * Whether a member is in the draft.
     *
     * Read off `draft.member_id` rather than off `chosenMember`, because the
     * draft is what is actually sent — a display object that disagreed with it
     * would be the more comfortable of the two to trust and the wrong one.
     *
     * @returns {boolean}
     */
    hasChosenMember() {
      return typeof this.draft.member_id === "number";
    },

    /** @returns {boolean} true when the collapsed chosen row is what renders */
    showsChosenMember() {
      return this.hasChosenMember && !this.changingMember;
    },

    /** @returns {boolean} */
    memberQueryIsLongEnough() {
      return this.memberQuery.trim().length >= MIN_SEARCH_CHARACTERS;
    },

    /** @returns {string} the chosen member's name, or an empty string */
    chosenMemberName() {
      return this.memberName(this.chosenMember);
    },

    /**
     * What the impression block is currently showing: `empty`, `draft` or `long`.
     *
     * ONE computed rather than three template branches, following
     * NewsComposer.vue's visibility summary: three branches is three places for
     * a fourth state to fall through and render nothing at all.
     *
     * `empty` covers BOTH half-filled cases on purpose. Half a lock-screen
     * preview would state a shape the phone will not produce, and the one
     * instruction names both fields.
     *
     * @returns {('empty'|'draft'|'long')}
     */
    impressionState() {
      if (this.draft.title.trim() === "" || this.draft.body.trim() === "") {
        return "empty";
      }

      if (this.draft.title.length > LONG_TITLE || this.draft.body.length > LONG_BODY) {
        return "long";
      }

      return "draft";
    },

    /**
     * Whether the draft is complete enough to confirm (IC6).
     *
     * FOUR CONDITIONS, and the in-flight state is the fifth — bound on the
     * button itself rather than folded in here, so this computed keeps meaning
     * "the draft is complete" and can be read as such from a test.
     *
     * The category condition is the one IC6's table does not list, and it comes
     * from B6's own `error` row: without labels the group renders empty and the
     * button stays disabled, because a category that cannot be NAMED cannot be
     * chosen. It fails CLOSED, which is the right direction for a send that
     * cannot be recalled.
     *
     * @returns {boolean}
     */
    canSend() {
      if (this.draft.title.trim() === "" || this.draft.body.trim() === "") {
        return false;
      }

      if (this.showsMemberPicker && !this.hasChosenMember) {
        return false;
      }

      return this.hasSelectableCategory;
    },

    /**
     * Whether leaving this page would throw away something unsent (B15).
     *
     * TWO CONDITIONS. The draft holds content — a title OR a body, because a
     * half-typed message is exactly the case the guard exists to catch — AND no
     * send has succeeded on this page. A composer whose send already succeeded
     * has nothing UNSENT to lose, even though the same text is still on screen.
     *
     * @returns {boolean}
     */
    hasUnsentDraft() {
      if (this.result !== null) {
        return false;
      }

      return this.draft.title.trim() !== "" || this.draft.body.trim() !== "";
    },

    /**
     * B11's body: one sentence when the counts matched, another when they did not.
     *
     * IT NEVER ROUNDS, NEVER HIDES THE SMALLER NUMBER AND NEVER SAYS `ongeveer`.
     * Both integers are interpolated separately, so a body that printed one
     * number twice would be visibly wrong rather than plausibly right.
     *
     * @returns {string}
     */
    resultBody() {
      if (this.result === null) {
        return "";
      }

      const sent = this.result.sent;
      const confirmed = this.result.confirmed;

      if (sent === confirmed) {
        return sent + " " + this.recipientNoun(sent)
          + ". De uitslag komt in het overzicht te staan.";
      }

      return "Verstuurd naar " + sent + " " + this.recipientNoun(sent) + ". "
        + "Bij het bevestigen waren dat er " + confirmed + ". "
        + "De groep is tussen bevestigen en versturen veranderd. "
        + "Dit verschil staat in het overzicht.";
    },

    /**
     * B12's heading, one of three, selected by the failure kind.
     *
     * @returns {string}
     */
    failureHeading() {
      if (this.failure === null) {
        return "";
      }

      if (this.failure.kind === "rate_limited") {
        return "Je hebt net al een bericht verstuurd";
      }

      if (this.failure.kind === "refused_text") {
        return "Dit bericht kan niet verstuurd worden";
      }

      return "Het bericht is niet verstuurd";
    },

    /**
     * B12's body for the two AUTHORED cases.
     *
     * The refused-text case is not here: it renders the server's own field
     * messages, straight from the error bag, and this screen authors nothing
     * for it.
     *
     * @returns {string}
     */
    failureBody() {
      if (this.failure === null) {
        return "";
      }

      if (this.failure.kind === "rate_limited") {
        const minutes = this.failure.retryAfterMinutes;

        if (minutes === null) {
          // `Retry-After` is a HEADER the 429 body does not carry, so a refusal
          // that arrives without it can name no number at all. Authored Dutch
          // rather than a guessed minute count.
          return "Probeer het over een paar minuten opnieuw.";
        }

        // `minuut` for one. 44-UI-SPEC.md's B12 row writes `{n} minuten` as a
        // template; D-44-11 sets the window to 10 minutes, so a one-minute wait
        // is reachable and `Wacht 1 minuten` is not Dutch. Same singular/plural
        // discipline as `ontvanger`/`ontvangers` a few lines up.
        return "Wacht " + minutes + " " + (minutes === 1 ? "minuut" : "minuten")
          + " en probeer het opnieuw.";
      }

      return "Controleer je internetverbinding en probeer het opnieuw.";
    },
  },

  created() {
    //
  },

  mounted() {
    this.fetchAudienceDescriptions();
    this.fetchCategories();
    this.installLeaveGuard();
  },

  beforeUnmount() {
    // The debounce must not outlive the page: a timer firing into a destroyed
    // component is a leak, and this is the only timer on the screen.
    this.clearMemberSearchTimer();

    // Neither may the guard. See releaseLeaveGuard()'s own docblock: the route
    // entry is shared by every visit.
    this.releaseLeaveGuard();
  },

  methods: {
    /**
     * The server's sentence for one audience, or an empty string.
     *
     * An empty string is what framework7-vue's list-item tests before rendering
     * the second line (`list-item.js:107`), so a missing description produces no
     * element at all — never a blank line, and never the string JavaScript
     * prints for an undefined value.
     *
     * @param {string} audience
     * @returns {string}
     */
    descriptionFor(audience) {
      const description = this.audienceDescriptions[audience];

      return typeof description === "string" ? description.trim() : "";
    },

    /**
     * Loads the four server-owned descriptions.
     *
     * THE FAILURE PATH IS THE DESIGNED ONE, not a fallback. A failed fetch
     * leaves four NAMED rows with no second line: no toast of its own, no error
     * block, and nothing disabled. The identifiers this page sends are
     * client-side, so the composer still works and the operator can still
     * choose. This is phase 43's rule — a supplementary fetch must not put a red
     * bar over a working page — applied to a working composer.
     *
     * The handler is required rather than decorative: `broadcasts/fetch_audiences`
     * carries no `.catch` by module rule, so without one here the rejection goes
     * unhandled on top of the one message the global handler already showed.
     */
    fetchAudienceDescriptions() {
      // RETURNS the promise, so a test can assert that it RESOLVES. The screen
      // renders identically whether the rejection is handled or left to escape,
      // so no assertion on the DOM can tell the two apart — the promise is the
      // only observable difference, and `mounted()` ignores it either way.
      return this.$store
        .dispatch("broadcasts/fetch_audiences")
        .then((rows) => {
          const map = {};

          (rows || []).forEach((row) => {
            if (row && typeof row.audience === "string") {
              map[row.audience] = row.description;
            }
          });

          this.audienceDescriptions = map;
        })
        .catch(() => {
          // Deliberately silent, and deliberately NOT clearing anything: the
          // rows keep whatever they already had rather than losing it to a
          // failure that changed nothing.
        });
    },

    /**
     * Loads the category taxonomy through the endpoint phase 39 already ships.
     *
     * ONE ENDPOINT FOR THE LABELS, not a second copy of the taxonomy. The action
     * resolves rather than rejects on failure and sets its own `load_failed`, so
     * an empty list here means either "not answered yet" or "answered badly" —
     * and both leave the group as its title alone with the button disabled,
     * which is the same correct outcome.
     */
    fetchCategories() {
      return this.$store.dispatch("notification_preferences/fetch_all");
    },

    /**
     * Writes the audience into the draft.
     *
     * Assigns to the draft and NOWHERE ELSE. IC1 forbids the choice reaching
     * Vuex, localStorage or anything else that outlives this page.
     *
     * @param {string} audience one of the four identifiers
     */
    selectAudience(audience) {
      this.draft.audience = audience;
    },

    /**
     * Writes the category into the draft.
     *
     * @param {string} category one of `operational`, `news`, `marketing`
     */
    selectCategory(category) {
      this.draft.category = category;
    },

    /**
     * A member's name as one string, with no trailing space when a part is
     * missing.
     *
     * `last_name` is nullable in `users`, and joining blindly would render
     * `Sam ` — a name with a hole after it that no operator can tell from a
     * rendering bug.
     *
     * @param {?Object} member
     * @returns {string}
     */
    memberName(member) {
      if (!member) {
        return "";
      }

      return [member.first_name, member.last_name]
        .map((part) => (typeof part === "string" ? part.trim() : ""))
        .filter((part) => part !== "")
        .join(" ");
    },

    /**
     * Handles a keystroke in the search field.
     *
     * DEBOUNCED, NOT POLLED. The timer is cleared on every keystroke, fires at
     * most once per burst, and is cleared again on unmount. There is no
     * repeating interval anywhere in this component.
     *
     * @param {Event} event
     */
    onMemberQueryInput(event) {
      this.memberQuery = event && event.target ? event.target.value : "";
      this.memberSearchFailed = false;

      this.clearMemberSearchTimer();

      if (!this.memberQueryIsLongEnough) {
        // Below the threshold there is nothing to show and nothing pending, so
        // both flags go down together and the hint takes the region.
        this.memberResults = [];
        this.memberSearching = false;

        return;
      }

      // Raised HERE rather than inside searchMembers(), so the debounce window
      // is covered too. See the flag's own docblock.
      this.memberSearching = true;
      this.memberSearchTimer = setTimeout(this.searchMembers, SEARCH_DEBOUNCE_MS);
    },

    /**
     * Runs the search.
     *
     * Reuses `/users/search` — the endpoint `UsersSearch.vue:78-86` already
     * calls — and nothing else from that component. It commits no app-wide
     * loader, dispatches no scroll, never runs on mount and renders no pager.
     *
     * DELIBERATELY NOT SILENT. The failure block below IS this region's surface
     * and the component adds no message of its own, so the global handler's
     * toast is the one message — the same arrangement admin/news.vue ships for
     * the same block. This is the opposite of the audience descriptions, which
     * fail with no block at all and therefore suppress their toast.
     *
     * The retry button calls THIS method, which is what returns the region to
     * its preloader rather than spinning inside the failure block.
     */
    searchMembers() {
      this.clearMemberSearchTimer();

      if (!this.memberQueryIsLongEnough) {
        return;
      }

      this.memberSearching = true;
      this.memberSearchFailed = false;

      this.memberSearchToken += 1;
      const token = this.memberSearchToken;

      this.$store
        .dispatch("api/getEndpoint", {
          endpoint: "/users/search",
          data: { query: this.memberQuery.trim(), page: 1 },
        })
        .then((response) => {
          if (token !== this.memberSearchToken) {
            return;
          }

          const rows = response && response.data ? response.data.data : null;
          this.memberResults = Array.isArray(rows) ? rows : [];
        })
        .catch(() => {
          if (token !== this.memberSearchToken) {
            return;
          }

          this.memberResults = [];
          this.memberSearchFailed = true;
        })
        .finally(() => {
          // Guarded too: an overtaken request settling last must not take the
          // spinner down from under the search that overtook it.
          if (token === this.memberSearchToken) {
            this.memberSearching = false;
          }
        });
    },

    /**
     * Puts the chosen member's ID — and only the id — into the draft.
     *
     * D-44-01: the client sends the member id and the server validates it as one
     * existing member. A name, an email or a filter in the draft would be
     * something the server does not re-derive, which is the definition of a
     * widening vector.
     *
     * @param {Object} member a row from `/users/search`
     */
    chooseMember(member) {
      this.draft.member_id = member.id;
      this.chosenMember = member;
      this.changingMember = false;
    },

    /** Re-opens the search, leaving the previous choice in the draft. */
    changeMember() {
      this.changingMember = true;
    },

    /**
     * Empties everything the picker holds.
     *
     * Called when the audience leaves `individual`. The server ignores a stale
     * id on a bulk send — 44-04 proves it — but a stale value in the draft would
     * be visible in a request log and would read as an attempt.
     */
    resetMemberPicker() {
      this.clearMemberSearchTimer();

      this.draft.member_id = null;
      this.chosenMember = null;
      this.changingMember = false;
      this.memberQuery = "";
      this.memberResults = [];
      this.memberSearching = false;
      this.memberSearchFailed = false;
      // Invalidates any request still in flight, so its `.then` cannot repopulate
      // a picker the operator has already navigated away from.
      this.memberSearchToken += 1;
    },

    /** Clears the pending debounce, if there is one. */
    clearMemberSearchTimer() {
      if (this.memberSearchTimer !== null) {
        clearTimeout(this.memberSearchTimer);
        this.memberSearchTimer = null;
      }
    },

    /**
     * The ONE handler both message fields write through.
     *
     * One handler rather than two, so the two fields cannot drift apart in how
     * they are read out of the event. The value is assigned rather than merged:
     * an input carries the whole field, every time.
     *
     * NOT sanitised and not trimmed on the way in. The server validates and, for
     * the payload guard, refuses — and trimming as the operator types would move
     * the caret out from under them mid-sentence, which is the mistake
     * NewsComposerPage.vue documents at its own change handler.
     *
     * @param {('title'|'body')} field
     * @param {string} value
     */
    updateDraftField(field, value) {
      this.draft[field] = typeof value === "string" ? value : "";
    },

    /**
     * Presses the primary button: runs the CONFIRM phase (IC7 step 2).
     *
     * NOTHING IS SENT HERE. The server resolves the audience, counts it, records
     * the draft and hands back a token. The count travels one way — from the
     * server to the screen — and this client never computes, caches or defaults
     * one, which is what makes the delta at step 8 detectable at all.
     *
     * The guard on `confirming` is the second half of the double-submit
     * protection. The button is already disabled while a confirm is in flight;
     * this covers a press that arrives in the same tick, before the re-render.
     */
    requestConfirmation() {
      if (this.confirming || !this.canSend) {
        return;
      }

      this.confirming = true;

      // CLEARED BEFORE THE REQUEST, never after it. A failure block still on
      // screen while the next attempt is in flight can be read as belonging to
      // that attempt, which is the same mistake NewsComposerPage.vue:544 avoids
      // with its sanitisation report.
      this.failure = null;

      this.$store
        // A COPY, never the draft itself. `callApi` writes `app_version` into
        // `payload.data` IN PLACE, so handing over the reactive draft would let
        // the transport layer add a key to the component's own state — and the
        // request-shape assertion would go on passing, because it reads the
        // request rather than the component.
        .dispatch("broadcasts/confirm", { ...this.draft })
        .then((confirmation) => {
          this.openCountConfirmation(confirmation);
        })
        .catch((rejection) => {
          // B10's `error` row: a failed confirm opens NO DIALOG and renders B12
          // on the composer. An operator never sees a dialog reporting a number
          // the server could not produce.
          //
          // `broadcasts/confirm` deliberately does NOT suppress its toast, so a
          // refused draft ALSO raises the global handler's message — which for a
          // 422 is the form request's own field bag. That is two surfaces for
          // one failure and it is the documented trade: IC8's one-message rule
          // is scoped to the SEND, and the persistent block is what survives the
          // five seconds the toast lasts.
          this.failure = this.describeFailure(rejection);
        })
        .finally(() => {
          this.confirming = false;
        });
    },

    /**
     * THE SEAM PLAN 44-10 FILLS: opens B10, the count confirmation dialog.
     *
     * =========================================================================
     *  THIS IS AN INTENTIONAL, DOCUMENTED GAP AND IT IS NOT A HIDDEN ONE.
     * =========================================================================
     * 44-09 owns everything the operator fills in BEFORE the confirmation gate.
     * The gate itself, the result block and the failure block are 44-10's work.
     * The method exists and is called with the server's own three scalars so the
     * seam is VISIBLE rather than implied — a `.then` that dropped the response
     * would leave the next plan guessing where the number was meant to arrive.
     *
     * Until 44-10 lands, pressing the button confirms and stops. Nothing is
     * sent: `/broadcasts/confirm` writes an unconfirmed row and returns a token
     * that expires in 15 minutes, and no message reaches anybody without the
     * second call. That is the safe direction for a half-built flow.
     *
     * When 44-10 fills it: `verticalButtons: true` is MANDATORY, not a
     * preference. `--f7-dialog-width` is 270px at every viewport and
     * `verticalButtons` defaults to false, so two horizontal buttons get ~135px
     * each and `Ja, versturen naar 280 ontvangers` cannot fit beside `Annuleren`.
     *
     * @param {{audience_size: number, recipient_count: number, confirmation_token: string}} confirmation
     */
    openCountConfirmation(confirmation) {
      const count = this.serverRecipientCount(confirmation);

      if (count === null) {
        // THE SERVER PRODUCED NO COUNT, so no dialog opens. B10's `error` row:
        // an operator never sees a dialog reporting a number the server could
        // not produce, and D-44-02 forbids this client defaulting one in its
        // place. The generic B12 body is the honest report — the request
        // succeeded and the answer was unusable, which is not something this
        // screen can describe more precisely without inventing a reason.
        this.failure = { kind: "other", retryAfterMinutes: null, fieldMessages: [] };

        return;
      }

      const variant = this.dialogVariant(confirmation);
      const token = confirmation.confirmation_token;

      // THE TOKEN LIVES IN THIS CLOSURE AND IN NO COMPONENT STATE. It is not a
      // column anywhere and exists only in the confirm response, so the only
      // thing that needs it is the button that spends it. Keeping it out of
      // `$data` is also what `BroadcastComposerForm.test.js` asserts.
      const dialog = f7.dialog.create({
        title: this.confirmationTitle(confirmation),
        text: this.confirmationBody(confirmation),

        // A backdrop tap here means "not now", which is the safe direction:
        // nothing has been sent and the draft is untouched. B15 is the opposite
        // case and sets this to false, because a dismissed dialog there would
        // leave Framework7's router queue waiting for ever.
        closeByBackdropClick: true,

        // MANDATORY, NOT A PREFERENCE. `--f7-dialog-width` is 270px at EVERY
        // viewport with no project override and `--f7-dialog-button-font-size`
        // is 17px, while `verticalButtons` defaults to false
        // (dialog-class.js:15). Two horizontal buttons get about 135px each,
        // and `Ja, versturen naar 280 ontvangers` cannot fit that. The shipped
        // delete dialog on the news composer does NOT set it; this one must.
        verticalButtons: true,

        // AND STACKING ALONE IS NOT ENOUGH, measured rather than assumed.
        // `.dialog-button` ships `padding: 0 5px` inside a border-box, so the
        // usable text box is 260px and `Ja, versturen naar 280 ontvangers`
        // renders as `Ja, versturen naar 280 ontvange…` — at 44-UI-SPEC.md's own
        // worked example. This class returns those ten pixels, scoped to this
        // dialog alone. See `src/css/components/dialogs.scss` for the readings.
        cssClass: "broadcast-count-dialog",

        buttons: this.confirmationButtons(confirmation, variant, count, token),
      });

      dialog.open();
    },

    /**
     * The server's recipient count, or `null` when the response carried none.
     *
     * NEVER DEFAULTED TO A NUMBER. D-44-02 makes the count come from the same
     * server-side resolution that performs the send, so this client must not
     * compute, cache or default one. A missing count is a contract regression
     * rather than a designed state, and the caller answers it by opening no
     * dialog at all — which is different from, and safer than, printing a zero
     * the server never said.
     *
     * @param {?Object} confirmation
     * @returns {?number}
     */
    serverRecipientCount(confirmation) {
      const count = confirmation ? confirmation.recipient_count : null;

      return typeof count === "number" && isFinite(count) ? count : null;
    },

    /**
     * Which of the five B10 variants to render: exactly one, every time.
     *
     * ===========================================================================
     *  KEYED ON `recipient_count`, NEVER ON `audience_size`. THIS IS THE BUG THE
     *  WHOLE SURFACE EXISTS TO AVOID.
     * ===========================================================================
     * The two numbers are NOT the same number on this stack. A confirm against
     * the test audience with a category nobody has opted into answers
     * `audience_size = 2` beside `recipient_count = 0`: the audience resolver
     * matched two super admins, and the reachability filter then dropped both.
     * A branch keyed on `audience_size` would fall straight through to a
     * populated variant reading `Naar 0 ontvangers` with a live send button
     * under it.
     *
     * @param {Object} confirmation
     * @returns {('zero'|'test'|'all_active'|'booked'|'individual')}
     */
    dialogVariant(confirmation) {
      if (this.serverRecipientCount(confirmation) === 0) {
        return "zero";
      }

      return this.draft.audience === "booked_today_tomorrow" ? "booked" : this.draft.audience;
    },

    /**
     * `ontvanger` for one, `ontvangers` for anything else.
     *
     * `ontvanger` and NEVER `lid`. One noun covers all four audiences — the test
     * audience is super admins and `individual` is one member — and the count is
     * who RECEIVES rather than who matched the audience. The audience-size
     * sentence uses `leden`, because that one counts members. The two nouns mean
     * two different things on purpose.
     *
     * @param {number} count
     * @returns {string}
     */
    recipientNoun(count) {
      return count === 1 ? "ontvanger" : "ontvangers";
    },

    /**
     * The dialog title: the count inside a sentence, at the inherited 18px.
     *
     * A bare `280` is scannable and says nothing — it does not say WHO, and who
     * is the fact that cannot be recalled. No font size is authored here.
     *
     * @param {Object} confirmation
     * @returns {string}
     */
    confirmationTitle(confirmation) {
      const count = this.serverRecipientCount(confirmation);

      if (count === 0) {
        return "Niemand ontvangt dit bericht";
      }

      return "Naar " + count + " " + this.recipientNoun(count);
    },

    /**
     * The two-number honesty line, or an EMPTY STRING when the numbers match.
     *
     * `280 leden. 280 daarvan…` is a line that says one number twice, which is
     * noise. Framework7 places the dialog text only when it is truthy
     * (dialog-class.js:64-66), so an empty string here renders no element at
     * all rather than a blank row.
     *
     * @param {Object} confirmation
     * @returns {string}
     */
    audienceSentence(confirmation) {
      const size = confirmation ? confirmation.audience_size : null;
      const count = this.serverRecipientCount(confirmation);

      if (typeof size !== "number" || size === count) {
        return "";
      }

      return "In deze groep zitten " + size + " leden. "
        + count + " daarvan kunnen nu een pushbericht ontvangen.";
    },

    /**
     * The dialog body for one variant. Exactly one of five is ever produced.
     *
     * @param {Object} confirmation
     * @returns {string}
     */
    confirmationBody(confirmation) {
      const variant = this.dialogVariant(confirmation);

      if (variant === "zero") {
        return ZERO_RECIPIENT_TEXT;
      }

      if (variant === "test") {
        // No irreversibility line: this one reaches only the super admins who
        // are already looking at the screen, which is what the test audience is
        // for.
        return "Alleen jij en de andere super admins krijgen dit bericht.";
      }

      if (variant === "individual") {
        // The member is named IN FULL here, which is why B5's chosen row is
        // allowed to keep the framework's ellipsis: a truncated name on the
        // picker is recoverable, and this sentence is the last word before the
        // send.
        return "Alleen " + this.chosenMemberName + " krijgt dit bericht. " + IRREVERSIBLE_LINE;
      }

      const sentence = this.audienceSentence(confirmation);

      return (sentence === "" ? "" : sentence + " ") + IRREVERSIBLE_LINE;
    },

    /**
     * The dialog's buttons: ONE at zero, TWO otherwise, safe one first.
     *
     * ===========================================================================
     *  THE ZERO VARIANT OFFERS NO SEND CONTROL AT ALL.
     * ===========================================================================
     * `Verstuur naar 0 ontvangers` would do nothing, and a control that does
     * nothing teaches an operator to press through the dialog. `Sluiten` returns
     * to the composer with the draft intact.
     *
     * ===========================================================================
     *  RED ON `all_active` ONLY, AND THE COLOUR IS NEVER THE ONLY SIGNAL.
     * ===========================================================================
     * Red is this app's one cannot-be-undone affordance, and D-44-03's typed
     * confirmation was offered to the owner and declined — so the count and the
     * copy are the whole remaining protection. Painting every non-test send red
     * would drain the signal.
     *
     * MEASURED CONTRAST: red text on white at the inherited 17px dialog button
     * size is 4.00:1, which FAILS WCAG AA for normal text (4.5 is the bar). It
     * passes AA large text and AA non-text. The ratio is INHERITED rather than
     * introduced — `NewsComposerPage.vue:597-599` and `calendar.vue:329-332`
     * already ship exactly this `color: 'red'` dialog button. The mitigation is
     * that colour is never the only signal here: the button's own label states
     * the count and the verb, so a greyscale screen, a colour-vision deficiency
     * and a screen reader all carry the same warning the hue does.
     *
     * @param {Object} confirmation
     * @param {string} variant
     * @param {number} count the SERVER's number, rendered and never derived
     * @param {string} token the confirmation token, held in the closure alone
     * @returns {Array<Object>}
     */
    confirmationButtons(confirmation, variant, count, token) {
      if (variant === "zero") {
        return [{ text: "Sluiten" }];
      }

      const confirmButton = {
        text: "Ja, versturen naar " + count + " " + this.recipientNoun(count),
        onClick: () => {
          // DEFERRED BY ONE TICK, exactly as the shipped delete dialog defers.
          // dialog-class.js:92-95 runs this handler BEFORE it closes the dialog,
          // so dispatching here would put the one irreversible request in flight
          // underneath a dialog that is still on screen — and this dialog has no
          // spinner to explain the wait.
          this.$nextTick(() => {
            this.performSend(token);
          });
        },
      };

      if (variant === "all_active") {
        confirmButton.color = "red";
      }

      return [{ text: "Annuleren" }, confirmButton];
    },

    /**
     * IC7 step 6: spends the token. THIS IS THE IRREVERSIBLE CALL.
     *
     * The token is a parameter rather than component state, so nothing survives
     * the dialog that could be spent twice by a later code path.
     *
     * @param {string} token
     */
    performSend(token) {
      if (this.sending) {
        return;
      }

      this.sending = true;

      // Cleared again here, not only in `requestConfirmation`: a caller added
      // later could reach this method by another path, and a stale failure
      // beside a fresh result is the one combination that must never render.
      this.failure = null;

      this.$store
        .dispatch("broadcasts/send", { confirmation_token: token })
        .then((sendResult) => {
          // BOTH NUMBERS COME FROM THIS RESPONSE. Neither is the confirm
          // phase's number kept in a variable — that is what makes the delta
          // detectable rather than invisible (IC7, T-44-48).
          this.result = {
            sent: sendResult.sent_recipient_count,
            confirmed: sendResult.confirmed_recipient_count,
          };
          this.failure = null;
        })
        .catch((rejection) => {
          // B11 and B12 are never on screen together. A failed send has nothing
          // sent to report.
          this.failure = this.describeFailure(rejection);
          this.result = null;
        })
        .finally(() => {
          this.sending = false;
        });
    },

    /**
     * Classifies a rejection into exactly one of B12's three shapes.
     *
     * `api.js:157` rejects with `error.response` — the axios RESPONSE, not the
     * axios error — so `status`, `data` and `headers` are read straight off it.
     * A network failure has no response at all and rejects with the error, whose
     * `status` is undefined; that falls to `other`, which is correct.
     *
     * A 422 WITH AN EMPTY BAG FALLS TO `other` RATHER THAN RENDERING A HEADING
     * WITH NOTHING UNDER IT. The refused-text branch exists to show the server's
     * own field messages; with none to show it has nothing to say.
     *
     * @param {?Object} rejection
     * @returns {{kind: string, retryAfterMinutes: ?number, fieldMessages: Array<string>}}
     */
    describeFailure(rejection) {
      const status = rejection && typeof rejection.status === "number" ? rejection.status : null;

      if (status === 429) {
        return {
          kind: "rate_limited",
          retryAfterMinutes: this.retryAfterMinutes(rejection),
          fieldMessages: [],
        };
      }

      if (status === 422) {
        const fieldMessages = this.fieldMessagesFromErrorBag();

        if (fieldMessages.length > 0) {
          return { kind: "refused_text", retryAfterMinutes: null, fieldMessages };
        }
      }

      return { kind: "other", retryAfterMinutes: null, fieldMessages: [] };
    },

    /**
     * `Retry-After`, in SECONDS on the wire, converted to whole minutes.
     *
     * CEILING, NEVER FLOOR. A floor turns any wait under a minute into `0` and
     * tells the operator to retry straight into another refusal. A non-positive
     * or unparseable value yields null, and the block then renders its authored
     * fallback rather than a number nobody supplied.
     *
     * Three spellings are tried because the header arrives differently
     * depending on who built the response object: axios v1 exposes an
     * `AxiosHeaders` instance with a `get()`, a plain object from a test fixture
     * does not, and a hand-built object may carry the canonical capitalisation.
     *
     * @param {?Object} rejection
     * @returns {?number}
     */
    retryAfterMinutes(rejection) {
      const headers = rejection && rejection.headers ? rejection.headers : {};

      let raw = null;

      if (typeof headers.get === "function") {
        raw = headers.get("retry-after");
      }

      if (raw === null || typeof raw === "undefined") {
        raw = headers["retry-after"];
      }

      if (raw === null || typeof raw === "undefined") {
        raw = headers["Retry-After"];
      }

      const seconds = parseInt(raw, 10);

      if (!isFinite(seconds) || seconds <= 0) {
        return null;
      }

      return Math.ceil(seconds / 60);
    },

    /**
     * The server's own field messages, flattened out of the api error bag.
     *
     * READ FROM THE STORE'S BAG rather than from the rejection, because that bag
     * is the application's one definition of "the server refused a field":
     * `api.js:200-202` fills it UNCONDITIONALLY, before the promise rejects, and
     * suppressing the toast does not suppress it. `api.js:88` clears it at the
     * start of every request, so it cannot belong to an older one.
     *
     * The caller SNAPSHOTS the result into `failure`, so the block on screen
     * keeps its text when the next request clears the bag.
     *
     * @returns {Array<string>}
     */
    fieldMessagesFromErrorBag() {
      const bag = (this.$store.state.api && this.$store.state.api.error_bag) || {};
      const messages = [];

      Object.keys(bag).forEach((field) => {
        const value = bag[field];

        if (Array.isArray(value)) {
          value.forEach((message) => {
            if (typeof message === "string" && message.trim() !== "") {
              messages.push(message);
            }
          });

          return;
        }

        if (typeof value === "string" && value.trim() !== "") {
          messages.push(value);
        }
      });

      return messages;
    },

    /**
     * B11's one route onward.
     *
     * `navigate`, matching the FAB on `admin/broadcasts.vue`. The leave guard
     * does not fire on the way out, because a composer whose send has succeeded
     * holds nothing unsent to lose.
     */
    goToOverview() {
      this.f7router.navigate("/admin/broadcasts");
    },

    /**
     * Attaches the unsent-draft check to this page's own route entry (B15).
     *
     * ONE SEAM FOR ALL THREE WAYS OUT. Framework7 runs `from.route.beforeLeave`
     * inside `processRouteQueue` (modules/router/process-route-queue.js:50-60),
     * and both `navigate()` and `back()` go through it. So the navbar's back
     * link, the Android hardware back — which `capacitor-app.js:67-71` turns
     * into `currentView.router.back()` — and a panel entry that navigates the
     * main view all arrive here. Three separate handlers would be three places
     * for the check to drift apart.
     *
     * It is also the ONLY cancellable seam: `page:beforeout` fires too late to
     * stop anything.
     */
    installLeaveGuard() {
      const route = this.f7route && this.f7route.route;

      if (!route) {
        return;
      }

      this.guardedRoute = route;
      route.beforeLeave = this.beforeLeave;
    },

    /**
     * Detaches it again, so the hook cannot outlive this page.
     *
     * NOT OPTIONAL. The route entry is the object declared in `src/js/routes.js`
     * and is shared by every visit, so a closed-over handler left on it would
     * keep this instance alive and guard the NEXT visitor's navigation with the
     * previous author's draft. The identity check means a page that somehow
     * replaced the handler is not stripped of someone else's.
     */
    releaseLeaveGuard() {
      if (this.guardedRoute && this.guardedRoute.beforeLeave === this.beforeLeave) {
        delete this.guardedRoute.beforeLeave;
      }

      this.guardedRoute = null;
    },

    /**
     * The unsent-draft prompt.
     *
     * ONE ARGUMENT, NOT FOUR. `processQueue` calls this with a single context
     * object — `{ router, to, from, resolve, reject, direction, app }`
     * (process-route-queue.js:22-31) — which is what `NewsComposerPage.vue`
     * already takes. A `(to, from, resolve, reject)` signature would receive the
     * context as `to` and nothing else, so neither callback would ever fire and
     * the router would wait for ever.
     *
     * A CLEAN COMPOSER RESOLVES IMMEDIATELY AND SHOWS NOTHING, which is what
     * keeps the dialog meaningful on the occasions it does appear.
     *
     * When the draft is dirty this method calls NEITHER callback: Framework7 has
     * already set `allowPageChange = false` and one of the two buttons must be
     * pressed. That is also why the backdrop must not close this dialog.
     *
     * @param {{resolve: Function, reject: Function}} context the router queue's callbacks
     */
    beforeLeave(context) {
      if (!this.hasUnsentDraft) {
        context.resolve();

        return;
      }

      const dialog = f7.dialog.create({
        // `niet verstuurd`, never `niet opgeslagen`. There is no save on this
        // screen, and copying phase 43's wording would promise a draft that does
        // not exist anywhere.
        title: "Bericht niet verstuurd",
        text: "Je hebt een bericht getypt dat nog niet is verstuurd. Als je weggaat, is het weg.",

        // A dialog dismissed by tapping the backdrop would call neither handler,
        // and Framework7 has already set `allowPageChange = false` — so the
        // router would wait for ever and the app would stop navigating. One of
        // the two buttons must be pressed.
        closeByBackdropClick: false,

        // The same M3/M4 reason as B10: 270px at every viewport, 17px buttons.
        verticalButtons: true,

        buttons: [
          { text: "Blijven", onClick: () => context.reject() },
          { text: "Weggooien", color: "red", onClick: () => context.resolve() },
        ],
      });

      dialog.open();
    },
  },

  watch: {
    /**
     * Empties the picker whenever the audience stops being `individual`.
     *
     * A WATCHER rather than a line inside `selectAudience()`, deliberately: the
     * watcher covers every writer of `draft.audience`, present and future, so a
     * second selection path added later cannot leave a stale member id behind.
     *
     * @param {string} audience
     */
    "draft.audience": function (audience) {
      if (audience !== "individual") {
        this.resetMemberPicker();
      }
    },
  },
};
</script>

<style scoped>
/*
  THE SAME 672px admin/news.vue and admin/broadcasts.vue already ship, taken from
  --f7-card-expandable-tablet-width (670px). Two super-admin pages that capped at
  different widths would read as a mistake. Below 768px the page is full-bleed,
  exactly as every other page in the app is.
*/
@media (min-width: 768px) {
  .broadcast-column {
    max-width: 672px;
    margin-left: auto;
    margin-right: auto;
  }
}

/*
  IC12 / M1 + M2, SCOPED TO THE ONE LIST.

  `--f7-list-item-title-white-space` is `nowrap` at the framework's own root, and
  `Training vandaag of morgen` is 26 characters at 17px — it would ellipsise at
  320px. A clipped audience name is the single most dangerous truncation on this
  screen, because `Alle actieve lede…` and `Alleen test` must never be
  confusable. It wraps instead.

  `--f7-list-item-text-max-lines` is 2 at that same root, and the description is
  server-supplied and unbounded, so it would be cut mid-sentence at the third
  line. Three lines is what the longest shipped sentence needs.

  BOTH ARE SCOPED HERE AND NEITHER IS SET AT :root, exactly as
  reserveringen.scss scopes the same tokens. Setting either globally would change
  every list in the app for one screen; 44-08's mutation M18 moved this rule to
  :root and went red.
*/
.broadcast-audience-list {
  --f7-list-item-title-white-space: normal;
  --f7-list-item-text-max-lines: 3;
}

/*
  The same three-line clamp for the category descriptions. The longest one the
  server ships is 89 characters and wraps to three lines at 15px on 320px. The
  titles here are one word each, so they keep the inherited nowrap.
*/
.broadcast-category-list {
  --f7-list-item-text-max-lines: 3;
}

/*
  `.broadcast-member-list` DELIBERATELY CARRIES NO RULE, and the class exists so
  that is visible rather than merely absent.

  B5 `long-text`: the chosen row inherits the framework's `nowrap` on purpose. A
  truncated member name is recoverable in ONE tap on `Wijzigen`, and the count
  dialog names the member again in full before anything is sent. B4's override is
  there because a clipped AUDIENCE name is not recoverable — `Alle actieve lede…`
  and `Alleen test` must never be confusable — and that argument does not carry
  over to a name the operator can re-open.
*/

/*
  The Heading role, taken entirely from Framework7's own tokens, matching
  admin/broadcasts.vue and admin/news.vue. No numeric font size is authored
  anywhere on this page, and the `small` element appears nowhere: general.scss:1
  makes it a 0.65em MULTIPLIER rather than a size, which phase 43 measured
  compounding to 11.4px.
*/
/*
  B7's body field, 96px tall before the author types anything.

  Framework7's `resizable` textarea grows from ONE line. A push body is up to 480
  characters, and a one-line box invites a one-line announcement while hiding the
  rest of what the author already typed. 96px is roughly four lines at the
  inherited 16px input size, and it grows from there.

  96 is a multiple of 4, like every other value this phase authors.
*/
.broadcast-body-input :deep(textarea) {
  min-height: 96px;
}

/*
  The Heading role for the impression's own title line, and the Body role for its
  text — both taken entirely from Framework7's own tokens. No numeric font size
  is authored on this page.
*/
.broadcast-impression-title {
  font-size: var(--f7-block-title-font-size);
  font-weight: var(--f7-block-title-font-weight);
  line-height: var(--f7-block-title-line-height);
  margin-top: 0;
  margin-bottom: 8px;
}

.broadcast-impression-body {
  font-size: var(--f7-list-item-text-font-size);
  margin-top: 0;
  margin-bottom: 0;
  /* A push body is plain text with the author's own line breaks in it. */
  white-space: pre-wrap;
}

.broadcast-heading {
  font-size: var(--f7-block-title-font-size);
  font-weight: var(--f7-block-title-font-weight);
  line-height: var(--f7-block-title-line-height);
  margin-top: 0;
  margin-bottom: 8px;
}

/*
  B12's triangle, in the INHERITED secondary text colour and not in a warning
  hue. No warning colour exists in this phase: persistence is the signal, and
  the heading beside it says the same thing in words. The icon is decoration on
  a line that already reads as a failure, never the only carrier of that fact.
*/
.broadcast-failure-icon {
  color: var(--f7-list-item-text-text-color);
  margin-right: 4px;
}

/*
  The Body role for both blocks, from Framework7's own token. No numeric font
  size is authored anywhere on this page.
*/
.broadcast-failure-body,
.broadcast-result-body {
  font-size: var(--f7-list-item-text-font-size);
  margin-top: 0;
  margin-bottom: 8px;
}
</style>
